/*
 * Compléments des fiches couteau Wusaki — comportement de snippets/wusaki-complements.liquid.
 *
 * Un élément <wusaki-complements> par fiche, après le bouton d'achat : bouton gravure, vignettes,
 * pierre.
 * - « + » d'une vignette et « Ajouter » de la pierre : ajout DIRECT au panier (/cart/add.js), puis
 *   rechargement du tiroir sans l'ouvrir. Un second clic retire ce qui a été ajouté (/cart/change.js).
 *   Étui : même quantité que le couteau. Chiffon, huile, pierre : 1.
 *   L'état des boutons SUIT LE PANIER : recalé au chargement et à chaque cartUpdate du thème
 *   (FoxTheme.pubsub), donc aussi quand un article est retiré depuis le tiroir (retour du 07/10).
 * - « Ajouter + gravure » : la ligne gravure est ajoutée AVANT le couteau, puis le formulaire du
 *   couteau est soumis normalement : le thème ouvre le tiroir une seule fois, avec son animation
 *   habituelle (un rechargement après coup le faisait « sauter »). Si l'ajout du couteau échoue
 *   (événement product-ajax:error de ProductForm, assets/theme.js), la gravure est retirée.
 */
if (!customElements.get('wusaki-complements')) {
  class WusakiComplements extends HTMLElement {
    connectedCallback() {
      if (this.initialise) return;
      this.initialise = true;

      this.querySelectorAll('[data-wc-variant]').forEach((b) => b.addEventListener('click', () => this.basculer(b)));

      if (window.FoxTheme && FoxTheme.pubsub) {
        this.desabonnerPanier = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, (data) => {
          if (data && data.cart && Array.isArray(data.cart.items)) this.synchroniser(data.cart);
        });
      }
      fetch(`${this.racine()}cart.js`, { headers: { Accept: 'application/json' } })
        .then((r) => r.json())
        .then((panier) => this.synchroniser(panier))
        .catch(() => {});

      this.dialogue = this.querySelector('[data-wc-gravure]');
      if (this.dialogue) this.initGravure();

      this.surAjout = () => (this.gravureAjoutee = null);
      this.surErreur = () => this.retirerGravure();
      document.addEventListener('product-ajax:added', this.surAjout);
      document.addEventListener('product-ajax:error', this.surErreur);
    }

    disconnectedCallback() {
      if (this.desabonnerPanier) this.desabonnerPanier();
      document.removeEventListener('product-ajax:added', this.surAjout);
      document.removeEventListener('product-ajax:error', this.surErreur);
    }

    racine() {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    }

    poster(url, corps) {
      return fetch(`${this.racine()}${url}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(corps),
      }).then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.json();
      });
    }

    afficher(b, ajoute) {
      b.setAttribute('aria-pressed', ajoute ? 'true' : 'false');
      if (b.dataset.labelOn) b.textContent = ajoute ? b.dataset.labelOn : b.dataset.labelOff;
      const carte = b.closest('[data-wc-carte]');
      if (carte) carte.classList.toggle('is-choisi', ajoute);
    }

    /* Ligne du panier correspondant à un bouton : même variante, sans propriétés (pas une ligne gravure) */
    ligne(panier, id) {
      return panier.items.find((l) => l.variant_id === id && !(l.properties && Object.keys(l.properties).length));
    }

    /* Recale chaque bouton sur le contenu réel du panier */
    synchroniser(panier) {
      this.querySelectorAll('[data-wc-variant]').forEach((b) => {
        if (b.getAttribute('aria-busy') === 'true') return;
        const l = this.ligne(panier, parseInt(b.dataset.wcVariant, 10));
        if (l) {
          const deja = parseInt(b.dataset.wcAjoute || '0', 10);
          b.dataset.wcAjoute = String(deja > 0 && deja <= l.quantity ? deja : l.quantity);
        } else {
          delete b.dataset.wcAjoute;
        }
        this.afficher(b, !!l);
      });
    }

    /* « + » / « Ajouter » : ajoute au panier, ou retire au second clic ce qui a été ajouté */
    basculer(b) {
      if (b.getAttribute('aria-busy') === 'true') return;
      const id = parseInt(b.dataset.wcVariant, 10);
      const msg = this.querySelector('[data-wc-msg]');
      const retirer = b.getAttribute('aria-pressed') === 'true';
      b.setAttribute('aria-busy', 'true');
      let action;
      if (retirer) {
        const qte = parseInt(b.dataset.wcAjoute || '1', 10);
        action = fetch(`${this.racine()}cart.js`, { headers: { Accept: 'application/json' } })
          .then((r) => r.json())
          .then((panier) => {
            const ligne = this.ligne(panier, id);
            if (!ligne) return null;
            return this.poster('cart/change.js', { id: ligne.key, quantity: Math.max(0, ligne.quantity - qte) });
          });
      } else {
        const qte = b.dataset.wcQte === 'couteau' ? this.quantiteCouteau() : 1;
        b.dataset.wcAjoute = String(qte);
        action = this.poster('cart/add.js', { items: [{ id, quantity: qte }] });
      }
      action
        .then(() => {
          msg.hidden = true;
          this.afficher(b, !retirer);
          document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
        })
        .catch(() => {
          msg.textContent = this.dataset.msgErreurAjout;
          msg.hidden = false;
        })
        .finally(() => b.removeAttribute('aria-busy'));
    }

    /* Formulaire d'ajout du couteau (bloc buy_buttons de la même fiche) */
    formulaire() {
      const fiche = this.closest('product-info, .product__info-container, .product') || document;
      return fiche.querySelector('form[data-type="add-to-cart-form"]') || document.querySelector('form[data-type="add-to-cart-form"]');
    }

    quantiteCouteau() {
      const form = this.formulaire();
      const champ = form && ((form.id && document.querySelector(`input[name="quantity"][form="${form.id}"]`)) || form.querySelector('input[name="quantity"]'));
      const q = champ ? parseInt(champ.value, 10) : 1;
      return q > 0 ? q : 1;
    }

    initGravure() {
      const d = this.dialogue;
      const msg = d.querySelector('[data-wc-gravure-msg]');
      this.querySelector('[data-wc-gravure-ouvrir]').addEventListener('click', () => {
        msg.hidden = true;
        d.showModal();
        document.documentElement.classList.add('wc-gravure-ouverte');
      });
      d.addEventListener('close', () => document.documentElement.classList.remove('wc-gravure-ouverte'));
      d.querySelector('[data-wc-gravure-fermer]').addEventListener('click', () => d.close());
      // Clic sur le fond : fermeture
      d.addEventListener('click', (e) => {
        if (e.target === d) d.close();
      });
      d.querySelector('[data-wc-gravure-form]').addEventListener('submit', (e) => {
        e.preventDefault();
        const texte = d.querySelector('[data-wc-gravure-texte]').value.trim();
        if (!texte) {
          msg.textContent = d.dataset.msgTexte;
          msg.hidden = false;
          d.querySelector('[data-wc-gravure-texte]').focus();
          return;
        }
        const form = this.formulaire();
        const bouton = form && form.querySelector('[type="submit"]');
        if (!form || !bouton || bouton.disabled) {
          msg.textContent = d.dataset.msgIndispo;
          msg.hidden = false;
          return;
        }
        const properties = {};
        properties[d.dataset.cleTexte] = texte;
        properties[d.dataset.clePolice] = d.querySelector('[data-wc-gravure-police]').value;
        properties[d.dataset.cleCouteau] = d.dataset.couteau;
        const valider = d.querySelector('[data-wc-gravure-form] [type="submit"]');
        valider.disabled = true;
        this.poster('cart/add.js', { items: [{ id: parseInt(d.dataset.variant, 10), quantity: 1, properties }] })
          .then((res) => {
            this.gravureAjoutee = res.items && res.items[0] ? res.items[0].key : null;
            d.close();
            form.requestSubmit ? form.requestSubmit() : bouton.click();
          })
          .catch(() => {
            msg.textContent = this.dataset.msgErreurAjout;
            msg.hidden = false;
          })
          .finally(() => (valider.disabled = false));
      });
    }

    /* Couteau refusé (rupture…) : on retire la gravure ajoutée juste avant */
    retirerGravure() {
      const cle = this.gravureAjoutee;
      if (!cle) return;
      this.gravureAjoutee = null;
      this.poster('cart/change.js', { id: cle, quantity: 0 })
        .then(() => document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } })))
        .catch(() => {});
    }
  }
  customElements.define('wusaki-complements', WusakiComplements);
}
